import {
  Avatar,
  Box,
  Container,
  HStack,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { signOut } from "firebase/auth";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../auth/auth-context/auth-context";
import { auth } from "../auth/config";
import { Routes } from "../helpers/router";
import { useKiosk } from "../kiosk/kiosk-context";
import { resolveSupportedLanguage } from "../localization/supported-language";
import logoImg from "../logo.svg";
import ReactCountryFlag from "react-country-flag";

import { Button } from "../components/chackra-ui-button";

type Props = {
  children: React.ReactNode | React.ReactNode[];
  profile?: {
    name: string;
    avatarUrl?: string | null;
  };
  maxWidth?: "container.sm" | "container.md" | "container.lg" | "full";
  bg?: string;
};

export const AppContainer = ({
  children,
  profile,
  maxWidth = "container.sm",
  bg,
}: Props) => {
  const { user } = useAuth();
  const { i18n, t } = useTranslation();
  const { isKiosk, hasNativeShell } = useKiosk();

  const languageOptions = [
    { code: "es", label: "Español", countryCode: "ES" },
    { code: "en", label: "English", countryCode: "GB" },
  ];
  const activeLanguageCode = resolveSupportedLanguage(
    i18n.resolvedLanguage ?? i18n.language,
  );
  const activeLanguage =
    languageOptions.find((option) => option.code === activeLanguageCode) ??
    languageOptions[0];

  const handleLanguageChange = (newLanguage: string) => {
    const language = resolveSupportedLanguage(newLanguage);
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
  };

  useEffect(() => {
    document.documentElement.lang = resolveSupportedLanguage(
      i18n.resolvedLanguage ?? i18n.language,
    );
  }, [i18n.language, i18n.resolvedLanguage]);

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg={bg}>
      {!hasNativeShell && (
        <Box
          as="nav"
          position="sticky"
          top={0}
          bg="white"
          zIndex={10}
          borderBottomWidth="1px"
          borderBottomColor="slate.200"
          height={{ base: 14, md: 16 }}
        >
          <HStack
            alignItems="center"
            justify="space-between"
            px={{ base: 4, md: 8 }}
            h="100%"
          >
            <a href={"/?nocache=" + new Date().getTime()}>
              <Image h={{ base: 5, md: 6 }} src={logoImg} />
            </a>
            <HStack spacing={3}>
              {isKiosk && (
                <Text fontSize="12px" color="slate.400">
                  {APP_VERSION}
                </Text>
              )}

              {/* Language selector */}
              <Menu>
                <MenuButton
                  as={Button}
                  variant="ghost"
                  borderRadius="10px"
                  h="40px"
                  px={3}
                  leftIcon={
                    <ReactCountryFlag
                      countryCode={activeLanguage.countryCode}
                      svg
                      style={{ width: "1.4em", height: "1.4em" }}
                      aria-label={activeLanguage.label}
                    />
                  }
                  rightIcon={
                    <Icon boxSize={4} color="slate.400">
                      <ChevronDownIcon />
                    </Icon>
                  }
                >
                  <Text fontSize="16px" fontWeight={500} color="#13192E">
                    {activeLanguage.label}
                  </Text>
                </MenuButton>
                <MenuList borderRadius="10px" minW="180px">
                  {languageOptions.map((option) => (
                    <MenuItem
                      key={option.code}
                      onClick={() => handleLanguageChange(option.code)}
                      fontSize="16px"
                      py={3}
                    >
                      <HStack spacing={3}>
                        <ReactCountryFlag
                          countryCode={option.countryCode}
                          svg
                          style={{ width: "1.4em", height: "1.4em" }}
                          aria-label={option.label}
                        />
                        <Text>{option.label}</Text>
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>

              {/* User menu */}
              {user ? (
                profile ? (
                  <Menu>
                    <MenuButton
                      h="40px"
                      px={2}
                      borderRadius="10px"
                      _hover={{ bg: "slate.50" }}
                      transition="background 0.15s"
                    >
                      <HStack spacing={2}>
                        <Avatar
                          size="sm"
                          name={profile.name}
                          src={profile.avatarUrl ?? undefined}
                        />
                        <Icon boxSize={4} color="slate.400">
                          <ChevronDownIcon />
                        </Icon>
                      </HStack>
                    </MenuButton>
                    <MenuList borderRadius="10px" minW="200px">
                      <MenuItem
                        as={RouterLink}
                        to={Routes.Profiles()}
                        fontSize="16px"
                        py={3}
                      >
                        {t("appContainer.changeProfile")}
                      </MenuItem>
                      <MenuDivider />
                      <MenuItem
                        onClick={() => signOut(auth)}
                        fontSize="16px"
                        py={3}
                      >
                        {t("appContainer.signOut")}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                ) : (
                  <Button
                    variant="ghost"
                    borderRadius="10px"
                    h="40px"
                    px={4}
                    onClick={() =>
                      signOut(auth).then(() => {
                        if (isKiosk) {
                          i18n.changeLanguage("es");
                          document.documentElement.lang = "es";
                        }
                      })
                    }
                  >
                    <Text fontSize="16px" fontWeight={500} color="#13192E">
                      {t("appContainer.signOut")}
                    </Text>
                  </Button>
                )
              ) : (
                <Button
                  variant="ghost"
                  borderRadius="10px"
                  h="40px"
                  px={4}
                  as={RouterLink}
                  to={Routes.Login()}
                >
                  <Text fontSize="16px" fontWeight={500} color="#13192E">
                    {t("appContainer.signIn")}
                  </Text>
                </Button>
              )}
            </HStack>
          </HStack>
        </Box>
      )}

      <Container
        maxW={maxWidth}
        minH={
          hasNativeShell
            ? "100vh"
            : {
                base: "calc(100vh - var(--chakra-sizes-14))",
                md: "calc(100vh - var(--chakra-sizes-16))",
              }
        }
        display="flex"
        flexDirection="column"
        position="relative"
        py={{ base: 4, md: 8 }}
      >
        {children}
      </Container>
    </Box>
  );
};
